(function () {
  'use strict';

  if (customElements.get('bundle-builder')) return;

  var MAX_LINE_QTY = 10;
  var LIVE_ANNOUNCE_DELAY = 700;

  /**
   * <bundle-builder> — build-a-bundle section controller.
   *
   * Every price string the shopper sees is either rendered server-side
   * (per-variant, in the JSON payload) or formatted client-side from cents
   * using shop.money_format from that same payload — JS never invents a
   * currency format. Tier discounts are DISPLAY ONLY; checkout pricing is
   * the merchant's matching automatic discount.
   *
   * Add-to-cart mirrors assets/cart-cro-add.js: one POST with an items
   * array + drawer section ids, then publish(cartUpdate) and
   * <cart-drawer>.renderContents(state), which repaints and opens the
   * drawer through the Section Rendering API.
   */

  function requestConfig() {
    if (typeof window.fetchConfig === 'function') return window.fetchConfig();
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function cartAddRoute() {
    var routes = window.routes || {};
    return routes.cart_add_url || '/cart/add';
  }

  function findDrawer() {
    return document.querySelector('cart-drawer');
  }

  function sectionIdsFor(drawer) {
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];
    return drawer.getSectionsToRender().map(function (section) {
      return section.id;
    });
  }

  // Dawn-style money formatter. The format template comes from
  // shop.money_format via the section's JSON payload.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    var value = '';
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  // Writes text only when it changed, so repeated recomputes do not thrash
  // the DOM (or spam assistive tech).
  function setText(element, text) {
    if (element && element.textContent !== text) element.textContent = text;
  }

  function setHidden(element, hidden) {
    if (!element) return;
    if (hidden !== element.hasAttribute('hidden')) element.toggleAttribute('hidden', hidden);
  }

  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
      }

      connectedCallback() {
        var dataElement = this.querySelector('[data-bundle-data]');
        this.data = null;
        try {
          this.data = JSON.parse(dataElement ? dataElement.textContent : '');
        } catch (error) {
          this.data = null;
        }
        if (!this.data || !Array.isArray(this.data.products)) return;

        this.tiers = (this.data.tiers || [])
          .filter(function (tier) {
            return tier.qty > 0;
          })
          .sort(function (a, b) {
            return a.qty - b.qty;
          });

        this.productsByKey = {};
        this.data.products.forEach(
          function (product) {
            this.productsByKey[product.key] = product;
          }.bind(this)
        );

        this.lines = {}; // card key -> quantity
        this.liveTimer = null;
        this.busy = false;

        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);

        this.renderTierMarks();
        this.syncAllCards();
        this.recompute();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        if (this.liveTimer) {
          clearTimeout(this.liveTimer);
          this.liveTimer = null;
        }
      }

      /* ------------------------------------------------------ data access */

      selectedVariantFor(card) {
        var product = this.productsByKey[card.dataset.cardKey];
        if (!product) return null;

        var select = card.querySelector('[data-variant-select]');
        var variantId = select ? select.value : card.dataset.variantId;

        for (var i = 0; i < product.variants.length; i += 1) {
          if (String(product.variants[i].id) === String(variantId)) return product.variants[i];
        }
        return product.variants[0] || null;
      }

      cards() {
        return Array.prototype.slice.call(this.querySelectorAll('[data-bundle-card]'));
      }

      /* --------------------------------------------------------- handlers */

      onClick(event) {
        var toggle = event.target.closest('[data-summary-toggle]');
        if (toggle && this.contains(toggle)) {
          this.toggleSummary(toggle);
          return;
        }

        var addButton = event.target.closest('[data-bundle-add]');
        if (addButton && this.contains(addButton)) {
          event.preventDefault();
          this.addBundle(addButton);
          return;
        }

        var step = event.target.closest('[data-step]');
        if (!step || !this.contains(step)) return;

        var card = step.closest('[data-bundle-card]');
        if (!card) return;

        var key = card.dataset.cardKey;
        var current = this.lines[key] || 0;
        var next = step.dataset.step === 'up' ? current + 1 : current - 1;
        next = Math.max(0, Math.min(MAX_LINE_QTY, next));

        var variant = this.selectedVariantFor(card);
        if (next > current && (!variant || !variant.available)) return;

        if (next === 0) {
          delete this.lines[key];
        } else {
          this.lines[key] = next;
        }

        this.setError('');
        this.syncCard(card);
        this.recompute();
      }

      onChange(event) {
        var select = event.target.closest('[data-variant-select]');
        if (!select || !this.contains(select)) return;

        var card = select.closest('[data-bundle-card]');
        if (!card) return;

        var variant = this.selectedVariantFor(card);
        if ((!variant || !variant.available) && this.lines[card.dataset.cardKey]) {
          delete this.lines[card.dataset.cardKey];
        }

        this.setError('');
        this.syncCard(card);
        this.recompute();
      }

      toggleSummary(toggle) {
        var summary = this.querySelector('.bundle-builder__summary');
        if (!summary) return;
        var isOpen = summary.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        toggle.setAttribute('aria-label', isOpen ? 'Hide bundle details' : 'Show bundle details');
      }

      /* ------------------------------------------------------- rendering */

      syncAllCards() {
        this.cards().forEach(this.syncCard.bind(this));
      }

      syncCard(card) {
        var variant = this.selectedVariantFor(card);
        var qty = this.lines[card.dataset.cardKey] || 0;
        var available = Boolean(variant && variant.available);

        card.classList.toggle('is-soldout', !available);
        card.classList.toggle('is-selected', qty > 0);

        if (variant) setText(card.querySelector('[data-card-price]'), variant.priceFormatted);
        setHidden(card.querySelector('[data-card-soldout]'), available);

        var countBadge = card.querySelector('[data-card-count]');
        setText(countBadge, String(qty));
        setHidden(countBadge, qty === 0);

        setText(card.querySelector('[data-qty]'), String(qty));

        var down = card.querySelector('[data-step="down"]');
        var up = card.querySelector('[data-step="up"]');
        if (down) down.toggleAttribute('disabled', qty === 0);
        if (up) up.toggleAttribute('disabled', !available || qty >= MAX_LINE_QTY);
      }

      renderTierMarks() {
        var marks = this.querySelector('[data-tier-marks]');
        if (!marks || !this.tiers.length) return;

        var maxQty = this.tiers[this.tiers.length - 1].qty;
        marks.textContent = '';
        this.tiers.forEach(function (tier) {
          var mark = document.createElement('span');
          mark.className = 'bundle-builder__progress-mark';
          mark.style.left = Math.round((tier.qty / maxQty) * 100) + '%';
          mark.textContent = tier.qty + ' · ' + tier.percent + '%';
          marks.appendChild(mark);
        });
      }

      state() {
        var totalQty = 0;
        var subtotal = 0;
        var items = [];

        this.cards().forEach(
          function (card) {
            var qty = this.lines[card.dataset.cardKey] || 0;
            if (qty === 0) return;
            var variant = this.selectedVariantFor(card);
            var product = this.productsByKey[card.dataset.cardKey];
            if (!variant || !product) return;

            totalQty += qty;
            subtotal += variant.price * qty;
            items.push({ product: product, variant: variant, qty: qty });
          }.bind(this)
        );

        var currentTier = null;
        var nextTier = null;
        this.tiers.forEach(function (tier) {
          if (totalQty >= tier.qty) currentTier = tier;
          else if (!nextTier) nextTier = tier;
        });

        var percent = currentTier ? currentTier.percent : 0;
        var discount = Math.round((subtotal * percent) / 100);

        return {
          totalQty: totalQty,
          subtotal: subtotal,
          items: items,
          currentTier: currentTier,
          nextTier: nextTier,
          percent: percent,
          discount: discount,
          total: subtotal - discount,
        };
      }

      money(cents) {
        return formatMoney(cents, this.data.moneyFormat);
      }

      progressMessage(state) {
        if (!this.tiers.length) return '';

        if (!state.nextTier && state.currentTier) {
          return 'Top saving unlocked — ' + state.currentTier.percent + '% off.';
        }

        var target = state.nextTier;
        if (!target) return '';
        var remaining = target.qty - state.totalQty;

        if (state.totalQty === 0) {
          var noun = target.qty === 1 ? 'item' : 'items';
          return 'Add ' + target.qty + ' ' + noun + ' to save ' + target.percent + '%.';
        }

        var message = 'Add ' + remaining + ' more to save ' + target.percent + '%';
        message += state.currentTier ? ' — ' + state.currentTier.percent + '% already unlocked.' : '.';
        return message;
      }

      recompute() {
        var state = this.state();

        // Item count
        setText(
          this.querySelector('[data-item-count]'),
          state.totalQty + ' ' + (state.totalQty === 1 ? 'item' : 'items')
        );

        // Tier pills (live in the section header, outside this element)
        var section = this.closest('.bundle-builder') || this;
        Array.prototype.forEach.call(section.querySelectorAll('[data-tier-pill]'), function (pill) {
          var active = state.currentTier && Number(pill.dataset.tierQty) === state.currentTier.qty;
          pill.classList.toggle('is-active', Boolean(active));
        });

        // Progress
        var progressText = this.progressMessage(state);
        setText(this.querySelector('[data-progress-text]'), progressText);
        var fill = this.querySelector('[data-progress-fill]');
        if (fill && this.tiers.length) {
          var maxQty = this.tiers[this.tiers.length - 1].qty;
          var pct = Math.min(state.totalQty / maxQty, 1) * 100;
          fill.style.width = pct + '%';
        }

        // Chosen items list
        this.renderItems(state);

        // Totals — the struck compare figure only appears when a tier is
        // reached, and only as display: checkout pricing comes from the
        // merchant's automatic discount.
        setText(this.querySelector('[data-subtotal]'), state.totalQty ? this.money(state.subtotal) : '—');

        var discountRow = this.querySelector('[data-discount-row]');
        setHidden(discountRow, !(state.percent > 0 && state.totalQty > 0));
        if (state.percent > 0) {
          setText(this.querySelector('[data-discount-label]'), 'Bundle discount (' + state.percent + '%)');
          setText(this.querySelector('[data-discount-amount]'), '−' + this.money(state.discount));
        }

        var compare = this.querySelector('[data-compare]');
        var showCompare = state.percent > 0 && state.totalQty > 0;
        setHidden(compare, !showCompare);
        if (showCompare) setText(compare, this.money(state.subtotal));
        setText(this.querySelector('[data-total]'), state.totalQty ? this.money(state.total) : '—');

        // CTA
        var cta = this.querySelector('[data-bundle-add]');
        if (cta && !this.busy) cta.toggleAttribute('disabled', state.totalQty === 0);
        setText(this.querySelector('[data-cta-total]'), state.totalQty ? '— ' + this.money(state.total) : '');

        this.scheduleAnnounce(state, progressText);
      }

      renderItems(state) {
        var list = this.querySelector('[data-summary-items]');
        if (!list) return;

        list.textContent = '';

        if (!state.items.length) {
          var empty = document.createElement('li');
          empty.className = 'bundle-builder__empty-line';
          empty.textContent = 'Your bundle is empty.';
          list.appendChild(empty);
          return;
        }

        state.items.forEach(
          function (item) {
            var li = document.createElement('li');
            li.className = 'bundle-builder__item';

            var name = document.createElement('span');
            name.className = 'bundle-builder__item-name';
            var label = item.product.title;
            if (item.variant.title && item.variant.title !== 'Default Title') {
              label += ' — ' + item.variant.title;
            }
            name.textContent = label;

            var meta = document.createElement('span');
            meta.className = 'bundle-builder__item-meta';
            meta.textContent = '×' + item.qty + '  ' + this.money(item.variant.price * item.qty);

            li.appendChild(name);
            li.appendChild(meta);
            list.appendChild(li);
          }.bind(this)
        );
      }

      // Announce tier state + total once interactions settle, instead of on
      // every stepper click.
      scheduleAnnounce(state, progressText) {
        var live = this.querySelector('[data-live-region]');
        if (!live) return;

        var self = this;
        if (this.liveTimer) clearTimeout(this.liveTimer);
        this.liveTimer = setTimeout(function () {
          var message;
          if (state.totalQty === 0) {
            message = 'Bundle empty. ' + progressText;
          } else {
            message =
              state.totalQty +
              (state.totalQty === 1 ? ' item' : ' items') +
              ' in bundle, total ' +
              self.money(state.total) +
              '. ' +
              progressText;
          }
          setText(live, message);
        }, LIVE_ANNOUNCE_DELAY);
      }

      setError(message) {
        setText(this.querySelector('[data-bundle-error]'), message || '');
      }

      setBusy(button, isBusy) {
        this.busy = isBusy;
        if (!button) return;
        button.toggleAttribute('disabled', isBusy);
        button.classList.toggle('is-loading', isBusy);
        button.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      }

      /* ----------------------------------------------------- add to cart */

      addBundle(button) {
        if (this.busy) return;

        var state = this.state();
        if (!state.items.length) return;

        var payloadItems = state.items.map(function (item) {
          return { id: item.variant.id, quantity: item.qty };
        });

        this.setError('');
        this.setBusy(button, true);

        var self = this;
        var drawer = findDrawer();
        var fallbackError = this.dataset.errorText || 'Something went wrong. Please try again.';

        var config = requestConfig();
        config.body = JSON.stringify({
          items: payloadItems,
          sections: sectionIdsFor(drawer),
          sections_url: window.location.pathname,
        });

        fetch(cartAddRoute(), config)
          .then(function (response) {
            return response
              .json()
              .catch(function () {
                return null;
              })
              .then(function (body) {
                if (!response.ok || !body || body.status || body.errors) {
                  var message = fallbackError;
                  if (body) {
                    if (typeof body.description === 'string' && body.description) message = body.description;
                    else if (typeof body.errors === 'string' && body.errors) message = body.errors;
                    else if (typeof body.message === 'string' && body.message) message = body.message;
                  }
                  throw new Error(message || fallbackError);
                }
                return body;
              });
          })
          .then(function (cartState) {
            self.setBusy(button, false);

            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-builder',
                cartData: cartState,
              });
            }

            // Reset the built bundle — it now lives in the cart.
            self.lines = {};
            self.syncAllCards();
            self.recompute();

            if (drawer && typeof drawer.renderContents === 'function' && cartState && cartState.sections) {
              drawer.renderContents(cartState); // repaints and opens the drawer
            }
          })
          .catch(function (error) {
            // Sold-out mid-flow (422) lands here: surface Shopify's message
            // and leave every selection untouched.
            self.setBusy(button, false);
            self.setError((error && error.message) || fallbackError);
          });
      }
    }
  );
})();
